import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { Box, CircularProgress, Typography } from "@mui/material";
import { BaseFormPage } from "../../components/common/BaseForm";
import {
  useGetDotInspectionByIdQuery,
  useUpdateDotInspectionMutation,
} from "../../api/dotInspectionsApi";
import { useFormSubmission } from "../../hooks/useFormSubmission";
import DotInspectionFormLayout from "./components/DotInspectionFormLayout";
import { PendingFile } from "./components/DotInspectionPendingFilesSection";
import {
  dotInspectionValidationSchema,
  getInitialDotInspectionFormValues,
  DotInspectionFormValues,
  parseOptionalNumber,
} from "./validation/dotInspectionFormValidation";
import { UpdateDotInspectionDto } from "../../types/dotInspection";

const DotInspectionEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const inspectionId = Number(id);

  const {
    data: inspection,
    isLoading: isLoadingInspection,
    error: loadError,
  } = useGetDotInspectionByIdQuery(inspectionId, { skip: !inspectionId });

  const [updateDotInspection] = useUpdateDotInspectionMutation();
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [deletedDocIds, setDeletedDocIds] = useState<number[]>([]);
  const [docsInitialized, setDocsInitialized] = useState(false);

  useEffect(() => {
    if (inspection?.documents && !docsInitialized) {
      const existing: PendingFile[] = inspection.documents.map((doc) => ({
        id: `existing-${doc.id}`,
        existingDocId: doc.id,
        fileName: doc.originalFileName,
        fileSize: doc.fileSizeBytes,
        documentCategory: doc.documentCategory || "",
        url: doc.url,
        contentType: doc.contentType,
      }));
      setPendingFiles(existing);
      setDocsInitialized(true);
    }
  }, [inspection, docsInitialized]);

  const handlePendingFilesChange = (updated: PendingFile[]) => {
    const removed = pendingFiles
      .filter((pf) => pf.existingDocId && !updated.find((u) => u.id === pf.id))
      .map((pf) => pf.existingDocId!);
    if (removed.length > 0) {
      setDeletedDocIds((prev) => [...new Set([...prev, ...removed])]);
    }
    setPendingFiles(updated);
  };

  const {
    isSubmitting,
    submitError,
    successMessage,
    showValidationErrors,
    handleSubmit,
  } = useFormSubmission({
    successMessage: "DOT inspection updated successfully!",
    onSuccess: () => {
      setTimeout(() => navigate("/dot-inspections"), 1500);
    },
  });

  const initialValues: DotInspectionFormValues = inspection
    ? {
        inspectionDate: inspection.inspectionDate
          ? new Date(inspection.inspectionDate).toISOString().split("T")[0]
          : "",
        claimCompanyId: inspection.claimCompanyId,
        claimCompanyName: null,
        driverEmployeeId: inspection.driverEmployeeId,
        vehicleId: inspection.vehicleId,
        trailerId: inspection.trailerId,
        points: inspection.points ?? "",
        description: inspection.description || "",
        amount: inspection.amount ?? "",
        driverCharged: inspection.driverCharged,
      }
    : getInitialDotInspectionFormValues();

  const formik = useFormik<DotInspectionFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: dotInspectionValidationSchema,
    onSubmit: async (values) => {
      await handleSubmit(async () => {
        if (values.vehicleId == null) {
          throw new Error("Truck is required");
        }

        const dto: UpdateDotInspectionDto = {
          inspectionDate: values.inspectionDate,
          claimCompanyId: values.claimCompanyId,
          claimCompanyName: values.claimCompanyName,
          driverEmployeeId: values.driverEmployeeId,
          vehicleId: values.vehicleId,
          trailerId: values.trailerId,
          points: parseOptionalNumber(values.points),
          description: values.description || null,
          amount: parseOptionalNumber(values.amount),
          driverCharged: values.driverCharged,
        };

        const newFiles = pendingFiles.filter((pf) => pf.file);
        const files = newFiles.map((pf) => pf.file!);
        const fileCategories = newFiles.map((pf) => pf.documentCategory || "");

        return await updateDotInspection({
          id: inspectionId,
          dto,
          files,
          fileCategories,
          deletedDocumentIds: deletedDocIds,
        }).unwrap();
      });
    },
  });

  if (isLoadingInspection) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !inspection) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Failed to load DOT inspection. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <BaseFormPage
      title="Edit DOT Inspection"
      subtitle={`Editing inspection #${inspection.id}`}
      onBack={() => navigate("/dot-inspections")}
      formik={formik}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      showValidationErrors={showValidationErrors}
      submitButtonText="Save Changes"
      maxWidth="lg"
    >
      <DotInspectionFormLayout
        availableCompanies={inspection.availableCompanies || []}
        availableDrivers={inspection.availableDrivers || []}
        availableTrucks={inspection.availableTrucks || []}
        availableTrailers={inspection.availableTrailers || []}
        pendingFiles={pendingFiles}
        onPendingFilesChange={handlePendingFilesChange}
      />
    </BaseFormPage>
  );
};

export default DotInspectionEditPage;
